import { Box, Text } from '@shopify/polaris';
import { useEffect, useState } from 'react';

// Parte una etiqueta en hasta 2 líneas si supera `max` chars: busca el
// espacio más cercano al centro para repartir bien (p.ej. "Pijamas
// largos" → ["Pijamas", "largos"]). Si no hay espacios y es muy
// larga, trunca con elipsis. Cada línea queda también limitada por
// `max` (si una de las dos sigue siendo demasiado larga, se trunca).
const splitLabel = (s, max = 13) => {
  if (!s) return [''];
  if (s.length <= max) return [s];
  const trim = (x) => (x.length > max ? `${x.slice(0, max - 1)}…` : x);
  const mid = s.length / 2;
  let bestSpace = -1;
  let bestDist = Infinity;
  for (let i = 0; i < s.length; i++) {
    if (s[i] === ' ') {
      const d = Math.abs(i - mid);
      if (d < bestDist) {
        bestDist = d;
        bestSpace = i;
      }
    }
  }
  if (bestSpace === -1) return [trim(s)];
  return [trim(s.slice(0, bestSpace)), trim(s.slice(bestSpace + 1))];
};

/**
 * Radar/spider chart con N ejes, un polígono por grupo. Normaliza cada
 * eje a su propio máximo (los ejes pueden estar en escalas muy distintas:
 * valor compra en € vs valor almacén en €, pero también compradas vs
 * vendidas en uds). Así se compara la FORMA — la proporción relativa
 * entre métricas — no las magnitudes absolutas.
 *
 *   data = [{ label, values: number[] }]  (values en orden de `axes`)
 *   axes = [{ label, formatValue }]
 */
/**
 * Radar chart con un pico por **categoría** (o subcategoría) y un
 * polígono por **métrica**. Pensado para series ya normalizadas (p.ej.
 * ratios % respecto de la compra) — así las magnitudes son
 * directamente comparables entre categorías y series.
 *
 * Normalización: **lineal, escala compartida** entre todas las series
 * visibles. El máximo global determina dónde llega el borde exterior.
 * Si todos los valores están en [0, 1.x], las anillos del grid se
 * leen directamente como % cuando las series son ratios.
 *
 *   axes   = (string | { primary, secondary })[]
 *            Si es objeto, se pinta en 2 líneas (`primary` arriba,
 *            `secondary` abajo en gris) y `primary + " · " + secondary`
 *            se usa como título del tooltip.
 *   series = [{ label, color, values, formatValue, totalValue? }]
 *            `values[i]` corresponde al eje `axes[i]`. `totalValue`
 *            (opcional) sustituye a Σ values en la leyenda "Totales".
 */
export const RadarChart = ({
  title,
  axes,
  axisGroups,
  series,
  helpText,
  legendHeader,
  legendFooter,
  // Tope superior de la escala radial. Por defecto 1.0 (100%) —
  // pensado para series que son ratios % sobre algo. Cuando los
  // valores son siempre pequeños (p.ej. share-of-total con muchas
  // categorías), conviene reducirlo para que la curva ocupe el
  // radar. Las gridlines y sus labels se recalculan en función de
  // este valor.
  scaleMax = 1.0,
  // Ancho mínimo del panel de la leyenda (columna 1fr). Sube si el
  // caller necesita meter contenido extra (p.ej. una mini-tabla)
  // bajo las filas de series via `legendFooter`.
  legendMinWidth = 220,
  // Lado de la leyenda. 'left' (default) → leyenda 1fr · chart 2fr.
  // 'right' → chart 2fr · leyenda 1fr (invertido).
  legendPosition = 'left',
}) => {
  const legendOnLeft = legendPosition === 'left';
  const size = 560;
  const padding = 100; // espacio para etiquetas de eje + arcos de grupo
  const radius = (size - padding * 2) / 2;
  const cx = size / 2;
  const cy = size / 2;
  const n = axes.length;
  // Eje sobre el que se muestra el tooltip lateral (hover). `null` =
  // ninguno.
  const [hoverIdx, setHoverIdx] = useState(null);
  // Si cambian los axes (p.ej. al pasar de Categorías a Subcategorías
  // o filtrar), el hoverIdx anterior podría apuntar a un índice que ya
  // no existe — `axes[hoverIdx]` sería undefined y reventaría al hacer
  // `.primary`. Reseteamos a null cuando cambia la longitud.
  useEffect(() => {
    setHoverIdx(null);
  }, [n]);
  // Series ocultas (toggle por click en la leyenda). Set de labels.
  const [hidden, setHidden] = useState(() => new Set());
  const toggleSeries = (label) =>
    setHidden((prev) => {
      const next = new Set(prev);
      if (next.has(label)) next.delete(label);
      else next.add(label);
      return next;
    });
  const isHidden = (label) => hidden.has(label);

  if (n < 3 || series.length === 0) {
    return (
      <Box>
        <Text variant="headingSm" as="h4">
          {title}
        </Text>
        <Box minHeight=".5rem" />
        <Text tone="subdued">Sin datos suficientes para un radar (mínimo 3 picos).</Text>
      </Box>
    );
  }

  // Escala radial. Las gridlines son siempre 5 anillos equiespaciados
  // (cada uno = 20% del scaleMax); las etiquetas muestran el % real
  // (= step * scaleMax). Si scaleMax = 1.0 los labels son 20/40/60/
  // 80/100; si scaleMax = 0.5 son 10/20/30/40/50.
  const SCALE_MAX = scaleMax;
  const GRID_STEPS = [0.2, 0.4, 0.6, 0.8, 1.0];

  // Coordenada en el eje `i` a fracción `t` (0..1) desde el centro.
  // Empezamos arriba (-π/2) y avanzamos en sentido horario.
  const pointAt = (i, t) => {
    const angle = -Math.PI / 2 + (i / n) * 2 * Math.PI;
    return { x: cx + Math.cos(angle) * radius * t, y: cy + Math.sin(angle) * radius * t };
  };

  // Escala lineal compartida, **clampada a 100%**. Las series son
  // ratios (% sobre la compra); el borde del radar es siempre el
  // 100%. Categorías con valores > 100% (p.ej. liquidación de stocks
  // antiguos) se clampan al borde y se marcan con un dot verde junto
  // a la etiqueta del eje correspondiente. La magnitud real sigue
  // visible en la leyenda y el tooltip.
  //
  // Guard explícito contra NaN/Infinity — sin esto, una división por
  // 0 (categoría sin compra) que escapara al `safeRatio` del caller
  // generaría puntos `NaN,NaN` que rompen el polígono SVG.
  const tFor = (vRaw) => {
    const v = Number(vRaw);
    if (!Number.isFinite(v) || v <= 0) return 0;
    return Math.min(1.0, v / SCALE_MAX);
  };

  // Ejes con overflow: cualquier serie no oculta y no refOnly cuyo
  // valor en ese eje supere 100%. Se marca con un dot verde junto a
  // la etiqueta del eje, para que el usuario vea que el polígono
  // está clampado al borde y consulte el valor real en la leyenda.
  const overflowsByAxis = axes.map((_, i) =>
    series.some(
      (s) =>
        !s.refOnly &&
        !isHidden(s.label) &&
        Number.isFinite(Number(s.values[i])) &&
        Number(s.values[i]) > SCALE_MAX,
    ),
  );

  // Runs de agrupamiento: ejes contiguos con misma `key` (gracias al
  // sort lexicográfico aguas arriba, los grupos salen ya contiguos).
  // Si sólo hay un run no merece la pena dibujar arcos — todos los
  // ejes serían del mismo color y no aportaría info.
  const runs = (() => {
    if (!axisGroups || axisGroups.length !== n) return [];
    const r = [];
    let start = 0;
    for (let i = 1; i <= n; i++) {
      if (i === n || axisGroups[i].key !== axisGroups[start].key)
        r.push({ start, end: i - 1, key: axisGroups[start].key, color: axisGroups[start].color });
      if (i < n && axisGroups[i].key !== axisGroups[start].key) start = i;
    }
    return r.length > 1 ? r : [];
  })();
  const hasGroups = runs.length > 0;

  const polygonOf = (sIdx) =>
    series[sIdx].values
      .map((vRaw, i) => {
        const p = pointAt(i, tFor(vRaw));
        return `${p.x.toFixed(1)},${p.y.toFixed(1)}`;
      })
      .join(' ');

  // Grid (4 anillos concéntricos).
  const gridPolygon = (t) =>
    axes
      .map((_, i) => {
        const p = pointAt(i, t);
        return `${p.x.toFixed(1)},${p.y.toFixed(1)}`;
      })
      .join(' ');

  return (
    <Box>
      <Text variant="headingSm" as="h4">
        {title}
      </Text>
      {helpText && (
        <>
          <Box minHeight=".25rem" />
          <Text variant="bodySm" tone="subdued">
            {helpText}
          </Text>
        </>
      )}
      <Box minHeight=".5rem" />
      {/* Layout en dos columnas: la leyenda (1fr) vive a un lado y
          el chart (2fr) al otro. Por defecto leyenda IZQUIERDA — si
          el caller pasa `legendPosition="right"`, invertimos las
          columnas del grid y dejamos la leyenda al final. */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: legendOnLeft ? 'minmax(0, 1fr) minmax(0, 2fr)' : 'minmax(0, 2fr) minmax(0, 1fr)',
          gap: 24,
          alignItems: 'start',
        }}
      >
        <div style={{ position: 'relative', justifySelf: 'center' }}>
          <svg width={size} height={size} onMouseLeave={() => setHoverIdx(null)}>
            {/* Grid concéntrico cada 20% de la escala fija 0–100%.
                Las anillas son la referencia visual; las etiquetas
                muestran 20%, 40%, …, 100%. */}
            {GRID_STEPS.map((t) => (
              <polygon key={t} points={gridPolygon(t)} fill="none" stroke="#E1E3E5" strokeWidth={t === 1 ? 1.2 : 0.6} />
            ))}
            {GRID_STEPS.map((t) => (
              <text key={`r-${t}`} x={cx + 3} y={cy - radius * t} fontSize={8} fill="#9CA3AF" dominantBaseline="middle">
                {`${Math.round(t * SCALE_MAX * 100)}%`}
              </text>
            ))}
            {/* Agrupamiento visual: dos radios punteados desde el
                centro al borde marcan los límites entre runs (grupos
                de ejes contiguos del mismo género/categoría). Y el
                nombre del grupo se imprime suave dentro del radar a
                0.8 del radio, en el ángulo medio del run. Si sólo
                hay un grupo, no se dibuja nada. */}
            {hasGroups && (
              <>
                {runs.map((run, runIdx) => {
                  // Frontera entre este run y el siguiente: a media
                  // rebanada después del último eje del run.
                  const axisAngle = (i) => -Math.PI / 2 + (i / n) * 2 * Math.PI;
                  const boundaryAng = axisAngle(run.end) + Math.PI / n;
                  const ex = cx + Math.cos(boundaryAng) * radius;
                  const ey = cy + Math.sin(boundaryAng) * radius;
                  return (
                    <line
                      key={`div-${runIdx}`}
                      x1={cx}
                      y1={cy}
                      x2={ex.toFixed(1)}
                      y2={ey.toFixed(1)}
                      stroke="#9CA3AF"
                      strokeWidth={0.8}
                      strokeDasharray="3 3"
                      opacity={0.7}
                    />
                  );
                })}
                {runs.map((run, runIdx) => {
                  const axisAngle = (i) => -Math.PI / 2 + (i / n) * 2 * Math.PI;
                  const startAng = axisAngle(run.start) - Math.PI / n;
                  const endAng = axisAngle(run.end) + Math.PI / n;
                  const midAng = (startAng + endAng) / 2;
                  const lx = cx + Math.cos(midAng) * radius * 0.8;
                  const ly = cy + Math.sin(midAng) * radius * 0.8;
                  return (
                    <text
                      key={`title-${runIdx}`}
                      x={lx.toFixed(1)}
                      y={ly.toFixed(1)}
                      fontSize={11}
                      fontWeight={600}
                      fill="#6B7280"
                      textAnchor="middle"
                      dominantBaseline="middle"
                      letterSpacing={1.5}
                      opacity={0.55}
                      style={{ pointerEvents: 'none' }}
                    >
                      {run.key.toUpperCase()}
                    </text>
                  );
                })}
              </>
            )}
            {/* Ejes radiales */}
            {axes.map((_, i) => {
              const p = pointAt(i, 1);
              const stroke = hoverIdx === i ? '#1F3A5F' : '#E1E3E5';
              const w = hoverIdx === i ? 1.4 : 0.6;
              return <line key={i} x1={cx} y1={cy} x2={p.x} y2={p.y} stroke={stroke} strokeWidth={w} />;
            })}
            {/* Polígonos: uno por serie. Pintados ANTES que los hot-areas
                para que estos últimos capten el hover encima. Series
                ocultas (click en la leyenda) se skipean. Series con
                `refOnly: true` (p.ej. "Entregado" = 100% en todos los
                ejes) sólo aparecen en la leyenda; el polígono sería
                redundante con la gridline del 100%.

                Orden de pintado: por suma de valores DESC. Así los
                polígonos grandes (p.ej. Venta total) quedan abajo y
                los pequeños (Venta campaña, Almacén) se ven ENCIMA.
                Sin esto, una serie cuyo polígono fuese siempre ≥ otra
                la enterraría visualmente. */}
            {(() => {
              // Filtramos refOnly y ocultas ANTES del sort. Ordenamos
              // por suma DESC (las grandes al fondo, las pequeñas
              // encima). Damos un stroke ligeramente más grueso a las
              // pintadas al final para que NO queden enterradas si
              // coinciden con una serie mayor por encima.
              const visible = series
                .map((s, sIdx) => ({ s, sIdx }))
                .filter(({ s }) => !s.refOnly && !isHidden(s.label))
                .map((entry) => ({
                  ...entry,
                  total: entry.s.values.reduce(
                    (a, v) => a + (Number.isFinite(Number(v)) ? Math.max(0, Number(v)) : 0),
                    0,
                  ),
                }))
                .sort((a, b) => b.total - a.total);
              return visible.map(({ s, sIdx }, drawIdx) => {
                // Última en pintarse = primera visible (la mayor).
                // Aumentamos el stroke ligeramente para cada
                // siguiente capa (pintada encima) — así si dos
                // polígonos coinciden visualmente, el más pequeño
                // sigue mostrando su borde.
                const sw = 1.6 + drawIdx * 0.4;
                return (
                  <polygon
                    key={s.label}
                    points={polygonOf(sIdx)}
                    fill={s.color}
                    fillOpacity={0.16}
                    stroke={s.color}
                    strokeWidth={sw}
                  />
                );
              });
            })()}
            {/* Etiquetas de eje (1 o 2 líneas) con hot-area transparente
                para hover. El círculo invisible da un área de impacto
                generosa para el ratón sin tocar el aspecto visual.
                  - Si el eje es string → una línea.
                  - Si es { primary, secondary } → dos líneas (primary
                    arriba en bold, secondary debajo en gris).
                */}
            {axes.map((axis, i) => {
              const p = pointAt(i, 1.12);
              const isHover = hoverIdx === i;
              const primary = typeof axis === 'string' ? axis : axis.primary;
              const secondary = typeof axis === 'string' ? null : axis.secondary;
              const anchor = p.x > cx + 5 ? 'start' : p.x < cx - 5 ? 'end' : 'middle';
              const baseline = p.y > cy + 5 ? 'hanging' : p.y < cy - 5 ? 'auto' : 'middle';
              // Líneas a renderizar: primary partido en 1 o 2 + (si
              // procede) un secondary opcional con estilo más gris.
              const primaryLines = splitLabel(primary || '', 13);
              const shortS =
                secondary && secondary.length > 14 ? `${secondary.slice(0, 13)}…` : secondary;
              const lines = [
                ...primaryLines.map((text) => ({ text, kind: 'primary' })),
                ...(shortS ? [{ text: shortS, kind: 'secondary' }] : []),
              ];
              const N = lines.length;
              const LH = 11;
              // Posicionamos cada línea respecto a p.y según el lado del
              // gráfico, manteniendo el bloque cohesionado:
              //   · top    (auto)    → la ÚLTIMA línea ancla en p.y
              //   · bottom (hanging) → la PRIMERA línea ancla en p.y
              //   · side   (middle)  → bloque centrado en p.y
              const yFor = (k) => {
                if (baseline === 'hanging') return p.y + k * LH;
                if (baseline === 'auto') return p.y - (N - 1 - k) * LH;
                return p.y + (k - (N - 1) / 2) * LH;
              };
              const overflows = overflowsByAxis[i];
              // Dot verde EN EL VÉRTICE (sobre el anillo del 100%).
              // Avisa de que algún polígono se sale de la escala y se
              // clampó al borde — la magnitud real está en la leyenda
              // y el tooltip.
              const vertex = pointAt(i, 1.0);
              return (
                <g key={i} onMouseEnter={() => setHoverIdx(i)} style={{ cursor: 'pointer' }}>
                  <circle cx={p.x} cy={p.y} r={26} fill="transparent" />
                  {overflows && (
                    <circle
                      cx={vertex.x}
                      cy={vertex.y}
                      r={5}
                      fill="#0F7B3E"
                      stroke="#FFFFFF"
                      strokeWidth={1.5}
                      style={{ pointerEvents: 'none' }}
                    />
                  )}
                  {lines.map((line, k) => (
                    <text
                      key={k}
                      x={p.x}
                      y={yFor(k).toFixed(1)}
                      fontSize={line.kind === 'primary' ? 10 : 9}
                      fontWeight={line.kind === 'primary' ? (isHover ? 800 : 600) : 500}
                      fill={line.kind === 'primary' ? '#1F3A5F' : '#6B7280'}
                      textAnchor={anchor}
                      dominantBaseline={baseline}
                      style={{ pointerEvents: 'none' }}
                    >
                      {line.text}
                    </text>
                  ))}
                </g>
              );
            })}
          </svg>
        </div>
        {/* Panel lateral fijo. Diseño único para los dos estados:
              · sin hover    → título "Totales" + valor = total agregado.
              · con hover    → título de la categoría/subcat + valor por
                                serie en esa categoría.
            Las filas son clicables: toggling oculta/muestra la serie
            correspondiente. Estado oculto se indica con el swatch
            vacío y el texto atenuado. Si el caller pasa
            `legendHeader`, se renderiza arriba (narrativa / etc). */}
        {/* `order: -1` cuando la leyenda va a la IZQUIERDA — empuja
            este div a la primera columna del grid aunque venga el
            segundo en el JSX. Con `legendPosition='right'` mantiene
            el orden natural (chart primero, leyenda después).
            `paddingTop: 20` lo baja respecto al borde del Card. */}
        <div
          style={{
            minWidth: legendMinWidth,
            fontSize: 12,
            order: legendOnLeft ? -1 : 0,
            paddingLeft: legendOnLeft ? 30 : 0,
            paddingRight: legendOnLeft ? 0 : 16,
            paddingTop: 20,
          }}
        >
          {legendHeader}
          <div
            style={{
              fontWeight: 700,
              color: '#1F3A5F',
              marginBottom: 8,
              fontSize: 13,
              borderBottom: '1px solid #E1E3E5',
              paddingBottom: 6,
            }}
          >
            {hoverIdx !== null
              ? typeof axes[hoverIdx] === 'string'
                ? axes[hoverIdx]
                : [axes[hoverIdx].primary, axes[hoverIdx].secondary].filter(Boolean).join(' · ')
              : 'Totales'}
          </div>
          {series.map((s) => {
            const off = isHidden(s.label);
            // Sin hover: usa `totalValue` / `totalRaw` (precomputados
            // por el caller como agregados del TOTAL del informe). Si
            // no hay totalValue, fallback a la suma de los visibles.
            const isHover = hoverIdx !== null;
            const pctValue = isHover
              ? s.formatValue(s.values[hoverIdx] ?? 0)
              : s.formatValue(
                  s.totalValue !== undefined ? s.totalValue : s.values.reduce((acc, v) => acc + (Number(v) || 0), 0),
                );
            // Valor absoluto (€ o uds) opcional — sólo si la serie lo
            // proporciona via `formatRaw` + `rawValues`/`totalRaw`.
            const hasRaw = typeof s.formatRaw === 'function' && (s.rawValues || s.totalRaw !== undefined);
            const rawValue = hasRaw ? s.formatRaw(isHover ? (s.rawValues?.[hoverIdx] ?? 0) : (s.totalRaw ?? 0)) : null;
            // Series `refOnly` (p.ej. "Entregado" = baseline 100%) no
            // tienen polígono que ocultar/mostrar — desactivamos el
            // toggle y el cursor pointer para evitar la confusión.
            const clickable = !s.refOnly;
            return (
              <div
                key={s.label}
                onClick={clickable ? () => toggleSeries(s.label) : undefined}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                  marginBottom: 6,
                  cursor: clickable ? 'pointer' : 'default',
                  userSelect: 'none',
                  opacity: off ? 0.45 : 1,
                }}
                title={clickable ? (off ? 'Mostrar capa' : 'Ocultar capa') : undefined}
              >
                <span
                  style={{
                    width: 14,
                    height: 14,
                    // Si la serie está oculta el swatch va transparente
                    // con borde — feedback inmediato del estado.
                    backgroundColor: off ? 'transparent' : s.color,
                    opacity: off ? 1 : 0.6,
                    border: `1.5px solid ${s.color}`,
                    borderRadius: 2,
                    display: 'inline-block',
                    flexShrink: 0,
                  }}
                />
                <span
                  style={{
                    color: '#6B7280',
                    flex: 1,
                    textDecoration: off ? 'line-through' : 'none',
                  }}
                >
                  {s.label}
                </span>
                <strong style={{ color: '#000', minWidth: 44, textAlign: 'right' }}>{pctValue}</strong>
                {rawValue && <span style={{ color: '#6B7280', minWidth: 80, textAlign: 'right' }}>{rawValue}</span>}
              </div>
            );
          })}
          {/* Bloque opcional debajo de las series — p.ej. narrativa
              automática que no debe interferir con los Totales. */}
          {legendFooter && <Box minHeight=".5rem" />}
          {legendFooter}
        </div>
      </div>
    </Box>
  );
};
